export const DELIVERY_TYPES = [
	{value: "door_to_door", label: "Door to door"},
	{value: "pickup", label: "Pickup"},
	{value: "express", label: "Express"},
];

export const ORDER_TYPES = [
	{value: 0, label: "Normal"},
	{value: 1, label: "Express"},
];

export const PAYMENT_METHODS = [
	{value: "cash", label: "Cash"},
	{value: "online", label: "Online"},
];

export const deliveryTypeLabel = (value) =>
	DELIVERY_TYPES.find((t) => t.value === value)?.label || value || "-";

/**
 * `type` is not an independent choice: it mirrors the fulfilment mode so the
 * legacy type-filtered lookup still selects the right rows. The seeder derives
 * it the same way, so it is computed here rather than asked for.
 */
export const orderTypeFor = (deliveryType) => (deliveryType === "express" ? 1 : 0);

/**
 * The DTO is mid-rename on the backend: the rate-matrix columns are
 * distance_to_km / weight_to_grams, but older responses still expose
 * deliveryMaxRange / maxWeightGrams. Read both so the page renders across the
 * transition instead of showing blank columns.
 */
export const readDistanceToKm = (row) => row?.distanceToKm ?? row?.deliveryMaxRange ?? null;
export const readWeightToGrams = (row) => row?.weightToGrams ?? row?.maxWeightGrams ?? null;

/**
 * Active is the assumption when the flag is missing: rules predating the
 * column are live, and showing them as disabled would misreport what the
 * pricing engine is actually using. Accepts the boolean, the 0/1 tinyint and
 * the string forms the API has returned across versions.
 */
export const readIsActive = (row) => {
	const value = row?.isActive ?? row?.is_active ?? row?.active;
	if (value === undefined || value === null || value === "") return true;
	return value !== 0 && value !== "0" && value !== false && value !== "false";
};

const isBlank = (v) => v === null || v === undefined || v === "";

const GRAMS_PER_KG = 1000;

export const formatDistanceTo = (km) => (isBlank(km) ? "Any" : `≤ ${Number(km)} km`);

export const formatAmountTo = (amount) =>
	isBlank(amount) ? "Any" : `≤ $${parseFloat(amount).toFixed(2)}`;

/**
 * Stored in grams, but the pricing sheet is written in kg — show both so a
 * five-figure gram value is readable at a glance.
 */
export const formatWeightTo = (grams) => {
	if (isBlank(grams)) return "Any";
	const g = Number(grams);
	return `≤ ${g.toLocaleString()} g (${(g / GRAMS_PER_KG).toLocaleString()} kg)`;
};

export const formatMoney = (value) =>
	isBlank(value) ? "-" : `$${parseFloat(value).toFixed(2)}`;

/**
 * A rule prices with a flat fee, a per-km rate, or both. Zero is meaningful,
 * so it is rendered rather than hidden as "unset".
 */
export const formatFee = (row) => {
	const parts = [];
	if (!isBlank(row.deliveryFeeFixed)) parts.push(`${formatMoney(row.deliveryFeeFixed)} fixed`);
	if (!isBlank(row.deliveryFeePerKm) && parseFloat(row.deliveryFeePerKm) !== 0) {
		parts.push(`${formatMoney(row.deliveryFeePerKm)}/km`);
	}
	return parts.length ? parts.join(" + ") : "-";
};

/**
 * Free delivery is expressed as a row, not a threshold: same distance and mode
 * as the paid bands but a higher amount ceiling, charging nothing.
 */
export const isFreeRow = (row) =>
	parseFloat(row.deliveryFeeFixed || 0) === 0 && parseFloat(row.deliveryFeePerKm || 0) === 0;

export const numberOrNull = (value, parse) =>
	isBlank(value) ? null : parse(value);
